
import React, { useMemo } from 'react';
import { useAttachments } from '@/context/AttachmentContext';
import AttachmentItem from './AttachmentItem';
import AttachmentFilters from './AttachmentFilters';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { Button } from '@/components/ui/button';
import { Download, Inbox, Loader2 } from 'lucide-react';
import { simplifyFileType } from '@/utils/fileTypeUtils';

const AttachmentList: React.FC = () => {
  const {
    filteredAttachments,
    isLoading,
    error,
    selected,
    filters,
    sortOption,
    previewOpen,
    previewAttachment,
    selectAttachment,
    deselectAttachment,
    selectAll,
    deselectAll,
    updateFilters,
    updateSortOption,
    downloadSelected,
    downloadAttachment,
    setPreviewOpen,
  } = useAttachments();
  
  // Extract unique file types for filter options
  const fileTypes = useMemo(() => {
    const types = filteredAttachments.map((att) => simplifyFileType(att.fileType, att.fileName));
    return [...new Set(types)].sort();
  }, [filteredAttachments]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.dateRange.start !== null ||
      filters.dateRange.end !== null ||
      filters.fileType.length > 0 ||
      filters.sizeRange.max !== null
    );
  }, [filters]);
  
  // Reset all filters
  const resetFilters = () => {
    updateFilters({
      search: '',
      dateRange: {
        start: null,
        end: null,
      },
      sender: '',
      fileType: [],
      sizeRange: {
        min: null,
        max: null,
      },
    });
  };
  
  if (isLoading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
        <p className="mt-4 text-muted-foreground">Loading attachments...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Error: {error.message}</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </div>
    );
  }
  
  if (filteredAttachments.length === 0) {
    return (
      <div>
        <AttachmentFilters
          filters={filters}
          onUpdateFilters={updateFilters}
          onSortChange={(field, direction) =>
            updateSortOption({ field, direction })
          }
          currentSortField={sortOption.field}
          currentSortDirection={sortOption.direction}
          fileTypes={fileTypes}
          hasActiveFilters={hasActiveFilters}
          resetFilters={resetFilters}
        />
        
        <div className="py-16 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Inbox className="h-8 w-8 text-muted-foreground" />
          </div>
          {hasActiveFilters ? (
            <>
              <h3 className="text-xl font-medium">No matching attachments</h3>
              <p className="mt-2 text-muted-foreground max-w-md">
                No attachments match your current filters. Try adjusting your search criteria.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={resetFilters}
              >
                Clear Filters
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-xl font-medium">No attachments found</h3>
              <p className="mt-2 text-muted-foreground max-w-md">
                We couldn't find any attachments in your Gmail account. Check back later or try refreshing.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-2xl font-medium">Attachments</h2>
          <p className="text-muted-foreground">
            {filteredAttachments.length} attachment{filteredAttachments.length !== 1 ? 's' : ''}
            {hasActiveFilters ? ' matching your filters' : ''}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={selected.size === filteredAttachments.length ? deselectAll : selectAll}
            className="text-sm"
          >
            {selected.size === filteredAttachments.length
              ? 'Deselect All'
              : `Select All (${filteredAttachments.length})`}
          </Button>
          
          <Button
            variant="default"
            size="sm"
            className="text-sm"
            onClick={() => downloadSelected()}
            disabled={selected.size === 0}
          >
            <Download className="mr-1.5 h-4 w-4" />
            Download Selected ({selected.size})
          </Button>
        </div>
      </div>
      
      <AttachmentFilters
        filters={filters}
        onUpdateFilters={updateFilters}
        onSortChange={(field, direction) =>
          updateSortOption({ field, direction })
        }
        currentSortField={sortOption.field}
        currentSortDirection={sortOption.direction}
        fileTypes={fileTypes}
        hasActiveFilters={hasActiveFilters}
        resetFilters={resetFilters}
      />
      
      <div className="space-y-3">
        {filteredAttachments.map((attachment) => (
          <AttachmentItem
            key={attachment.id}
            attachment={attachment}
            isSelected={selected.has(attachment.id)}
            onSelect={() => selectAttachment(attachment.id)}
            onDeselect={() => deselectAttachment(attachment.id)}
            onDownload={() => downloadAttachment(attachment.id)}
            onPreview={() => downloadAttachment(attachment.id, true)}
          />
        ))}
      </div>
      
      {/* Preview Modal */}
      <AttachmentPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        attachment={previewAttachment}
      />
    </div>
  );
};

export default AttachmentList;
